import React, {
  FC, MouseEventHandler, ReactNode, useId, KeyboardEventHandler,
} from 'react';
import classNames from 'classnames';
import { DISPLAY_DATE_FORMAT } from 'src/common/constants';
import Label from 'src/common/components/Label';
import Icon from 'src/common/components/IconDS22';
import newTZDate, { formatDate, formatDateRange } from 'src/common/utils/dateHelpers';
import { useTranslation } from 'next-i18next';
import { withSuffix } from 'src/common/utils/dataAttributeHelpers';
import useDateFnsLocale from 'src/common/hooks/useDateFnsLocale';
import { differenceInHours, startOfDay } from 'date-fns';
import { FULL_DAY_HOURS } from 'src/listing/constants';
import getClassNames from 'src/common/utils/getClassNames';
import styles from './DatesField.module.scss';

export type DatesFieldProps = {
  startDate: string | null;
  endDate?: string | null;
  label: string;
  inlineSlot?: ReactNode;
  labelSize?: 's' | 'm' | 'l';
  description?: string;
  clearLabel?: string;
  placeholder?: string;
  handleClearDates?: MouseEventHandler<HTMLButtonElement>;
  handleClick: (
    e: React.MouseEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>
  ) => void;
  required?: boolean;
  error?: string | boolean | null;
  withErrorIcon?: boolean;
  withErrorMessage?: boolean;
  showIcon?: boolean;
  'data-test'?: string;
  children?: ReactNode;
  id?: string;
};

const DatesField: FC<DatesFieldProps> = ({
  startDate,
  endDate,
  label,
  inlineSlot,
  labelSize = 'l',
  description,
  clearLabel,
  placeholder,
  handleClearDates,
  handleClick,
  required = true,
  error,
  withErrorIcon,
  withErrorMessage = true,
  showIcon = true,
  children,
  'data-test': dataTest,
  id,
}) => {
  const { t } = useTranslation('common');
  const locale = useDateFnsLocale();
  const generatedId = useId();
  const finalId = id || generatedId;
  const showClearDates = !!handleClearDates;
  const isSameDate = startDate && endDate
    && differenceInHours(
      startOfDay(newTZDate(endDate)),
      startOfDay(newTZDate(startDate)),
    ) < FULL_DAY_HOURS;

  const hasErrorMessage = typeof error === 'string' && withErrorMessage;

  const today = formatDate(new Date(), DISPLAY_DATE_FORMAT, locale);
  let defaultPlaceholder = `${today} - ${today}`;

  if (!endDate || isSameDate) {
    defaultPlaceholder = `${today}`;
  }
  const value = startDate && formatDateRange(
    newTZDate(startDate),
    endDate ? newTZDate(endDate) : null,
    locale,
  );

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleClick(e);
    }
  };

  return (
    <div className={styles.root}>
      <Label
        required={required}
        label={label}
        inlineSlot={inlineSlot}
        size={labelSize}
      >
        {description && <div className={styles.description}>{description}</div>}
        <span className={styles.label}>
          {showIcon && (
            <Icon
              className={getClassNames(styles, 'dateIcon', classNames({ error }))}
              id="calendar"
              size="l"
            />
          )}
          <div className={getClassNames(styles, 'input', classNames({ error, showIcon }))}>
            <input
              readOnly
              className={getClassNames(styles, 'inputField', classNames({ error }))}
              placeholder={placeholder || defaultPlaceholder}
              value={value || ''}
              onClick={handleClick}
              onKeyDown={handleKeyDown}
              tabIndex={0}
              role="button"
              aria-label={`${label}${required ? ' (required)' : ''}`}
              aria-describedby={error ? `${finalId}-error` : undefined}
              data-test={withSuffix(dataTest, 'date-field')}
              id={finalId}
            />
            {children && (
              <div>
                {children}
              </div>
            )}
          </div>
          {showClearDates && value && !clearLabel && (
            <button
              className={styles.clearButton}
              type="button"
              onClick={handleClearDates}
              aria-label={t('clear-date-field-label')}
            >
              <Icon id="x" />
            </button>
          )}
        </span>
        {hasErrorMessage && (
          <div className={styles.error} id={`${finalId}-error`}>
            {withErrorIcon && (
              <Icon
                id="warning-filled"
                size="s"
                className={styles.errorIcon}
              />
            )}
            {error}
          </div>
        )}
      </Label>
      {showClearDates && value && clearLabel && (
        <button
          type="button"
          onClick={handleClearDates}
          className={styles.clearLabelButton}
        >
          {clearLabel}
        </button>
      )}
    </div>
  );
};

export default DatesField;
